import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import type { ApiResponse } from '@/types';

export type EscalationOutcome = 'sent' | 'failed' | 'not_configured';

/** Where an escalation was configured: the protocol step, or the instruction. */
export interface EscalationSource {
	kind: 'protocol' | 'instruction';
	id: string;
	name: string | null;
	nodeId: string | null;
	nodeTitle: string | null;
}

/** One escalation action Kim ran and how it ended, as kept by the backend (escalationLog.service.ts). */
export interface EscalationLogEntry {
	id: string;
	buId: number | null;
	provider: string | null;
	ticketId: string | null;
	ticketUrl: string | null;
	channel: string | null;
	/** `send-email`, `assign`, `unassign`, `send-private-note`, `send-slack-message`, `unknown`, or the legacy `assign-to-human`. */
	action: string;
	outcome: EscalationOutcome;
	reason: string | null;
	/** The fields a not-configured action lacked, by name. */
	missing: string[];
	/** Why Kim escalated. */
	context: string | null;
	source: EscalationSource | null;
	requestId: string | null;
	traceId: string | null;
	at: string;
}

export interface EscalationLogFilter {
	outcome?: EscalationOutcome;
	/** ISO instants: at or after `from`, before `to`. */
	from?: string;
	to?: string;
}

export const escalationLogService = {
	/** A page of the brand's escalations, newest first; `before` is the last entry id already shown. */
	async fetchPage(filter: EscalationLogFilter, before: string | undefined, scope: { bu_id?: string }) {
		const res = await api.get<ApiResponse<{ entries: EscalationLogEntry[]; hasMore: boolean }>>(
			API_ENDPOINTS.ESCALATION_LOGS.LIST,
			{ params: { ...scope, ...filter, ...(before && { before }), limit: 25 } }
		);
		return res.data.data;
	},

	/** How the brand's escalations ended over the dates. */
	async fetchSummary(filter: Pick<EscalationLogFilter, 'from' | 'to'>, scope: { bu_id?: string }) {
		const res = await api.get<ApiResponse<{ counts: Record<EscalationOutcome, number> }>>(
			API_ENDPOINTS.ESCALATION_LOGS.SUMMARY,
			{ params: { ...scope, ...filter } }
		);
		return res.data.data.counts;
	}
};
